'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Loader2, Mail, Lock, User, Leaf, Shield, BarChart2, Sprout, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import PageLoader from '@/components/PageLoader';

type Role = 'farmer' | 'admin';

// Admin email must match: admin*@agroai.ai  (e.g. admin01@agroai.ai, admin_xyz@agroai.ai)
const ADMIN_EMAIL_REGEX = /^admin[^@]*@agroai\.ai$/i;

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  bgColor: string;
}

function getPasswordStrength(password: string): PasswordStrength {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  const normalized = Math.min(4, score);

  const levels: PasswordStrength[] = [
    { score: 0, label: 'খুব দুর্বল', color: 'text-red-600', bgColor: 'bg-red-500' },
    { score: 1, label: 'দুর্বল', color: 'text-red-500', bgColor: 'bg-red-400' },
    { score: 2, label: 'মাঝারি', color: 'text-amber-500', bgColor: 'bg-amber-400' },
    { score: 3, label: 'ভালো', color: 'text-green-500', bgColor: 'bg-green-400' },
    { score: 4, label: 'শক্তিশালী', color: 'text-green-600', bgColor: 'bg-green-600' },
  ];

  return levels[normalized];
}

interface PasswordRequirement {
  label: string;
  met: boolean;
}

function getRequirements(password: string): PasswordRequirement[] {
  return [
    { label: 'কমপক্ষে ৬ অক্ষর', met: password.length >= 6 },
    { label: 'কমপক্ষে ৮ অক্ষর (প্রস্তাবিত)', met: password.length >= 8 },
    { label: 'একটি বড় হাতের অক্ষর (A-Z)', met: /[A-Z]/.test(password) },
    { label: 'একটি সংখ্যা (0-9)', met: /[0-9]/.test(password) },
    { label: 'একটি বিশেষ চিহ্ন (!@#$...)', met: /[^A-Za-z0-9]/.test(password) },
  ];
}

export default function RegisterPage() {
  const router = useRouter();
  const { signUp, signInWithGoogle } = useAuth();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<Role>('farmer');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);

  const strength = getPasswordStrength(password);
  const requirements = getRequirements(password);

  const validateAdminEmail = (emailVal: string): boolean => {
    return ADMIN_EMAIL_REGEX.test(emailVal);
  };

  const triggerShake = () => {
    setShakeError(true);
    setTimeout(() => setShakeError(false), 500);
  };

  const setErrorWithShake = (msg: string) => {
    setError(msg);
    triggerShake();
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true); setError('');
    try {
      await signInWithGoogle(role);
    } catch (err: any) {
      setErrorWithShake(err?.message || 'Google লগইন ব্যর্থ হয়েছে।');
      setGoogleLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!fullName.trim()) { setErrorWithShake('পূর্ণ নাম দিন।'); return; }
    if (!email.trim()) { setErrorWithShake('ইমেইল দিন।'); return; }
    if (!password) { setErrorWithShake('পাসওয়ার্ড দিন।'); return; }
    if (password !== confirmPassword) { setErrorWithShake('পাসওয়ার্ড মিলছে না।'); return; }
    if (password.length < 6) { setErrorWithShake('পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।'); return; }

    // Admin email validation
    if (role === 'admin') {
      if (!validateAdminEmail(email)) {
        setErrorWithShake('অ্যাডমিন হিসেবে নিবন্ধনের জন্য শুধুমাত্র admin*@agroai.ai ফরম্যাটের ইমেইল গ্রহণযোগ্য। যেমন: admin01@agroai.ai');
        return;
      }
    }

    setLoading(true);

    // Pre-check: query user_profiles to see if email already exists
    try {
      const supabase = createClient();
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('email', email.trim().toLowerCase())
        .maybeSingle();

      if (existingProfile) {
        setErrorWithShake('এই ইমেইলে ইতিমধ্যে একটি অ্যাকাউন্ট আছে। লগইন করুন।');
        toast.error('এই ইমেইল ইতিমধ্যে নিবন্ধিত।', { position: 'top-right' });
        setLoading(false);
        return;
      }
    } catch {
      // If pre-check fails, proceed to signUp which will catch duplicates itself
    }

    try {
      await signUp(email, password, { fullName, role });
      setRegisterSuccess(true);
      toast.success('অ্যাকাউন্ট তৈরি হয়েছে! ইমেইল যাচাই করুন এবং লগইন করুন।', {
        description: 'Check your inbox to verify your email address.',
        duration: 5000,
      });
      setRedirecting(true);
      setTimeout(() => {
        router.push('/login');
      }, 1200);
    } catch (err: any) {
      const msg = err?.message || '';
      if (
        msg.includes('already registered') ||
        msg.includes('already been registered') ||
        msg.includes('User already registered') ||
        msg.includes('already exists') ||
        msg.includes('duplicate') ||
        msg.includes('unique constraint') ||
        err?.code === '23505'
      ) {
        setErrorWithShake('এই ইমেইলে ইতিমধ্যে একটি অ্যাকাউন্ট আছে। লগইন করুন।');
        toast.error('এই ইমেইল ইতিমধ্যে নিবন্ধিত।', { position: 'top-right' });
      } else if (msg.includes('Password should be')) {
        setErrorWithShake('পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।');
      } else {
        setErrorWithShake(msg || 'নিবন্ধন ব্যর্থ হয়েছে। আবার চেষ্টা করুন।');
      }
      setLoading(false);
    }
  };

  // Field validation states
  const nameValid = fullName.trim().length >= 2;
  const emailValid = email.includes('@') && email.includes('.');
  const emailAdminInvalid = role === 'admin' && email && !validateAdminEmail(email);
  const passwordValid = password.length >= 6;
  const confirmValid = confirmPassword.length > 0 && password === confirmPassword;
  const confirmMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Full-screen redirect loader overlay */}
      {redirecting && <PageLoader message="লগইন পেজে যাচ্ছেন" />}

      {/* Left: Form Panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-white overflow-y-auto">
        <div className="w-full max-w-md py-4">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <AppLogo size={40} />
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">AgroGuard AI</p>
              <h1 className="text-2xl font-extrabold text-gray-900 leading-tight">নিবন্ধন করুন</h1>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-6">
            ইতিমধ্যে অ্যাকাউন্ট আছে?{' '}
            <Link href="/login" className="text-green-600 font-semibold hover:underline">লগইন করুন</Link>
          </p>

          {/* Role selector */}
          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">আপনি কে?</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { setRole('farmer'); setError(''); }}
                className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all ${role === 'farmer' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${role === 'farmer' ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Leaf size={16} className={role === 'farmer' ? 'text-green-600' : 'text-gray-500'} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold">কৃষক</p>
                  <p className="text-xs opacity-70">Farmer</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => { setRole('admin'); setError(''); }}
                className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all ${role === 'admin' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${role === 'admin' ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  <Shield size={16} className={role === 'admin' ? 'text-amber-600' : 'text-gray-500'} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold">অ্যাডমিন</p>
                  <p className="text-xs opacity-70">Admin</p>
                </div>
              </button>
            </div>

            {/* Admin notice */}
            {role === 'admin' && (
              <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                <AlertCircle size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  অ্যাডমিন নিবন্ধনের জন্য শুধুমাত্র <strong>admin*@agroai.ai</strong> ফরম্যাটের ইমেইল গ্রহণযোগ্য।
                  <br />যেমন: <span className="font-mono">admin01@agroai.ai</span>
                </p>
              </div>
            )}
          </div>

          {/* Error with shake */}
          {error && (
            <div className={`bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-4 flex items-start gap-2 error-slide-in ${shakeError ? 'shake' : ''}`}>
              <AlertCircle size={15} className="text-red-500 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Success message */}
          {registerSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 mb-4 flex items-center gap-2 success-pop">
              <CheckCircle size={16} className="text-green-600 check-pop shrink-0" />
              <span>অ্যাকাউন্ট সফলভাবে তৈরি হয়েছে!</span>
            </div>
          )}

          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading || redirecting}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all text-sm font-semibold text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed mb-5"
          >
            {googleLoading ? (
              <Loader2 size={18} className="animate-spin text-gray-500" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2045C17.64 8.5663 17.5827 7.9527 17.4764 7.3636H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.2045Z" fill="#4285F4"/>
                <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
                <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.5931 3.68182 9C3.68182 8.4069 3.78409 7.83 3.96409 7.29V4.9582H0.957275C0.347727 6.1731 0 7.5477 0 9C0 10.4523 0.347727 11.8269 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                <path d="M9 3.5795C10.3214 3.5795 11.5077 4.0336 12.4405 4.9255L15.0218 2.344C13.4632 0.8918 11.4259 0 9 0C5.48182 0 2.43818 2.0168 0.957275 4.9582L3.96409 7.29C4.67182 5.1627 6.65591 3.5795 9 3.5795Z" fill="#EA4335"/>
              </svg>
            )}
            Google দিয়ে নিবন্ধন করুন
            {role === 'admin' && <span className="text-xs text-amber-600 font-normal">(Admin)</span>}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-medium">অথবা ইমেইল দিয়ে</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleRegister} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">পূর্ণ নাম <span className="text-red-500">*</span></label>
              <div className="relative">
                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="আপনার পূর্ণ নাম"
                  className={`w-full pl-10 pr-10 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all field-highlight ${
                    nameValid ? 'border-green-400 ring-2 ring-green-100 bg-green-50/20' : 'border-gray-200 focus:border-green-400 focus:ring-green-100'
                  }`}
                  required
                />
                {nameValid && (
                  <CheckCircle size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-green-500 check-pop" />
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                ইমেইল <span className="text-red-500">*</span>
                {role === 'admin' && <span className="ml-2 text-xs font-normal text-amber-600">(admin*@agroai.ai)</span>}
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={role === 'admin' ? 'admin01@agroai.ai' : 'আপনার ইমেইল'}
                  className={`w-full pl-10 pr-10 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all field-highlight ${
                    emailAdminInvalid
                      ? 'border-red-300 ring-2 ring-red-100 focus:border-red-400 focus:ring-red-100'
                      : emailValid
                      ? 'border-green-400 ring-2 ring-green-100 bg-green-50/20' :'border-gray-200 focus:border-green-400 focus:ring-green-100'
                  }`}
                  required
                />
                {emailValid && !emailAdminInvalid && (
                  <CheckCircle size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-green-500 check-pop" />
                )}
                {emailAdminInvalid && (
                  <AlertCircle size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-red-500" />
                )}
              </div>
              {emailAdminInvalid && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1 error-slide-in">
                  <AlertCircle size={11} /> অ্যাডমিন ইমেইল অবশ্যই admin*@agroai.ai ফরম্যাটে হতে হবে
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">পাসওয়ার্ড <span className="text-red-500">*</span></label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="কমপক্ষে ৬ অক্ষর"
                  className={`w-full pl-10 pr-12 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all field-highlight ${
                    password.length > 0 && strength.score >= 3
                      ? 'border-green-400 ring-2 ring-green-100 bg-green-50/20'
                      : password.length > 0 && strength.score < 2
                      ? 'border-red-300 ring-2 ring-red-100' :'border-gray-200 focus:border-green-400 focus:ring-green-100'
                  }`}
                  required
                />
                {passwordValid && strength.score >= 3 && (
                  <CheckCircle size={15} className="absolute right-9 top-1/2 -translate-y-1/2 text-green-500 check-pop" />
                )}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {/* Strength Bar */}
              {password.length > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                          i < strength.score ? strength.bgColor : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs font-semibold ${strength.color}`}>
                    পাসওয়ার্ড শক্তি: {strength.label}
                  </p>
                </div>
              )}
            </div>

            {/* Password Requirements */}
            {password.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
                {requirements.map((req, idx) => (
                  <div key={idx} className={`flex items-center gap-2 transition-all duration-200 ${req.met ? 'success-pop' : ''}`}>
                    {req.met ? (
                      <CheckCircle size={14} className="text-green-500 shrink-0 check-pop" />
                    ) : (
                      <XCircle size={14} className="text-gray-300 shrink-0" />
                    )}
                    <span className={`text-xs transition-colors duration-200 ${req.met ? 'text-green-700 font-medium' : 'text-gray-400'}`}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">পাসওয়ার্ড নিশ্চিত করুন <span className="text-red-500">*</span></label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="পাসওয়ার্ড আবার দিন"
                  className={`w-full pl-10 pr-10 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all field-highlight ${
                    confirmMismatch
                      ? 'border-red-300 ring-2 ring-red-100 focus:border-red-400 focus:ring-red-100'
                      : confirmValid
                      ? 'border-green-400 ring-2 ring-green-100 bg-green-50/20' :'border-gray-200 focus:border-green-400 focus:ring-green-100'
                  }`}
                  required
                />
                {confirmValid && (
                  <CheckCircle size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-green-500 check-pop" />
                )}
                {confirmMismatch && (
                  <AlertCircle size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-red-500" />
                )}
              </div>
              {confirmMismatch && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1 error-slide-in">
                  <AlertCircle size={11} /> পাসওয়ার্ড মিলছে না
                </p>
              )}
              {confirmValid && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1 success-pop">
                  <CheckCircle size={11} /> পাসওয়ার্ড মিলেছে
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || redirecting}
              className="w-full min-h-[48px] py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-95 btn-ripple"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : registerSuccess ? <CheckCircle size={18} className="check-pop" /> : null}
              {loading ? 'তৈরি হচ্ছে...' : registerSuccess ? 'সফল!' : 'অ্যাকাউন্ট তৈরি করুন'}
            </button>
          </form>
        </div>
      </div>

      {/* Right: Agricultural Imagery Panel */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=1200&q=80')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/65 via-green-800/40 to-transparent" />

        <div className="relative z-10 w-full h-full flex flex-col justify-between p-10">
          {/* Top card */}
          <div className="self-end max-w-xs">
            <div className="bg-gray-900/85 backdrop-blur-sm rounded-2xl p-4 text-white shadow-xl border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Sprout size={14} className="text-green-400" />
                <span className="text-xs text-gray-300 font-medium">AI ফসল বিশ্লেষণ</span>
              </div>
              <p className="text-sm font-bold mb-1">১০,০০০+ কৃষক ব্যবহার করছেন</p>
              <p className="text-xs text-green-400 font-semibold">বাংলাদেশ জুড়ে সক্রিয়</p>
            </div>
          </div>

          {/* Middle card */}
          <div className="self-start max-w-xs">
            <div className="bg-gray-900/85 backdrop-blur-sm rounded-2xl p-4 text-white shadow-xl border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <BarChart2 size={14} className="text-amber-400" />
                <span className="text-xs text-gray-300 font-medium">রোগ শনাক্তকরণ</span>
              </div>
              <div className="space-y-2 text-xs">
                {[
                  { label: 'ধানের ব্লাস্ট', pct: 85 },
                  { label: 'আলুর লেট ব্লাইট', pct: 72 },
                  { label: 'টমেটো ভাইরাস', pct: 91 },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between mb-0.5">
                      <span className="text-gray-300">{item.label}</span>
                      <span className="text-green-400 font-semibold">{item.pct}%</span>
                    </div>
                    <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-green-400 rounded-full" style={{ width: `${item.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom card */}
          <div className="self-end max-w-xs">
            <div className="bg-gray-900/85 backdrop-blur-sm rounded-2xl p-4 text-white shadow-xl border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Shield size={14} className="text-amber-400" />
                <span className="text-xs text-gray-300 font-medium">নিরাপদ প্ল্যাটফর্ম</span>
              </div>
              <p className="text-sm font-bold mb-1">কৃষক ও অ্যাডমিন</p>
              <p className="text-xs text-amber-400 font-semibold">ভূমিকা-ভিত্তিক অ্যাক্সেস</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
